/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type React from "react"
import { Table, Tag, Typography, Tooltip, Space, Button, type TablePaginationConfig, type TableProps } from "antd"
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import { useTableScroll } from "@/hooks/useTableScroll"
import { getDownloadUrl } from "./PreviewOutputModal"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

export const SOURCE_TYPE_MAP: Record<string, { label: string; color: string }> = {
  input_text: { label: "Thêm nhanh", color: "blue" },
  manual: { label: "Nhập thủ công", color: "cyan" },
  import_excel: { label: "Nhập Excel", color: "green" },
  import_anki: { label: "Nhập Anki", color: "orange" },
  import_anki_text: { label: "Nhập Anki Text", color: "gold" },
  import_quizlet: { label: "Nhập Quizlet", color: "geekblue" },
  ai_image: { label: "AI quét ảnh", color: "purple" },
  ai_pdf: { label: "AI quét PDF", color: "magenta" },
  ai_chat: { label: "AI Chat", color: "pink" },
  ai_extract_text: { label: "AI trích xuất", color: "volcano" },
  // legacy fallbacks
  quick: { label: "Thêm nhanh", color: "blue" },
  upload: { label: "Upload File", color: "green" },
  ai: { label: "AI Sinh", color: "purple" },
}

export const STATUS_MAP: Record<string, { label: string; color: string }> = {
  committed: { label: "Đã lưu", color: "success" },
  previewed: { label: "Bỏ cuộc", color: "warning" },
  failed: { label: "Lỗi file", color: "error" },
}

export interface AddVocabularyLogItem {
  id: number
  user_id?: number | null
  target_set_id?: number | null
  status: "previewed" | "committed" | "failed" | string
  error_message?: string | null
  source_type?: string | null
  raw_input_path?: string | null
  output_path?: string | null
  duration_ms?: number | null
  user_agent?: string | null
  ip_address?: string | null
  total_previewed_items?: number | null
  total_committed_items?: number | null
  ai_generated_count?: number | null
  dictionary_matched_count?: number | null
  payload?: {
    user_prompt?: string | null
    edit_prompts?: string[] | null
    [key: string]: any
  } | string | null
  created_at: string
  user?: {
    id: number
    name: string
    email: string
    username: string
    avatar?: string
    is_test?: number
  } | null
  set?: {
    id: number
    title: string
    name?: string
    slug?: string
    type?: string
    node_type?: string
  } | null
}

interface AddVocabularyTableProps {
  data: AddVocabularyLogItem[]
  loading: boolean
  onViewDetail: (record: AddVocabularyLogItem, index: number) => void
  pagination?: TableProps<AddVocabularyLogItem>["pagination"]
}

export const AddVocabularyTable: React.FC<AddVocabularyTableProps> = ({
  data,
  loading,
  onViewDetail,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<AddVocabularyLogItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      fixed: "left",
      render: (val: number) => <span className="font-mono text-gray-500 text-xs">#{val}</span>,
    },
    {
      title: "Người dùng",
      key: "user",
      width: 200,
      fixed: "left",
      render: (_, record) => (
        <UserCellLink user={record.user} userId={record.user_id} fallback={<Tag color="default">Khách</Tag>} />
      ),
    },
    {
      title: "Bộ từ vựng",
      dataIndex: ["set", "title"],
      key: "set_title",
      width: 200,
      render: (val, row) => {
        const title = val || row.set?.name
        if (!row.target_set_id && !title) {
          return <Text type="secondary">Chưa có bộ từ</Text>
        }
        if (!title) return <Text type="secondary">—</Text>
        return <span className="font-medium text-gray-800">{title}</span>
      },
    },
    {
      title: "Nguồn",
      dataIndex: "source_type",
      key: "source_type",
      width: 130,
      align: "center",
      render: (val?: string | null) => {
        if (!val) return <Text type="secondary">—</Text>
        const info = SOURCE_TYPE_MAP[val]
        if (info) {
          return <Tag color={info.color}>{info.label}</Tag>
        }
        return <Tag>{val}</Tag>
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (val: string, record) => {
        const statusKey = val || "previewed"
        const info = STATUS_MAP[statusKey]
        const tag = info ? <Tag color={info.color}>{info.label}</Tag> : <Tag>{val}</Tag>
        if (val === "failed" && record.error_message) {
          return (
            <Tooltip title={`Lỗi: ${record.error_message}`} placement="topLeft">
              <span className="cursor-pointer">{tag}</span>
            </Tooltip>
          )
        }
        return tag
      },
    },
    {
      title: "Số lượng từ",
      key: "word_counts",
      width: 160,
      align: "center",
      render: (_, record) => {
        const committed = record.total_committed_items ?? 0
        const previewed = record.total_previewed_items ?? 0
        const aiGen = record.ai_generated_count ?? 0
        const dictMatched = record.dictionary_matched_count ?? 0

        return (
          <Tooltip
            title={
              <div className="flex flex-col gap-1 py-1 text-xs">
                <div>
                  • <strong>Đã lưu:</strong> {committed} từ
                </div>
                <div>
                  • <strong>Đã xem trước:</strong> {previewed} từ
                </div>
                <div>
                  • <strong>AI Sinh:</strong> {aiGen} từ
                </div>
                <div>
                  • <strong>Có trong từ điển:</strong> {dictMatched} từ
                </div>
              </div>
            }
          >
            <div className="flex cursor-pointer select-none flex-col items-center justify-center">
              <div className="font-mono font-semibold text-xs">
                <span className="text-emerald-600">{committed}</span>
                <span className="text-gray-400"> / </span>
                <span className="text-gray-700">{previewed}</span>
                <span className="ml-1 font-normal text-[11px] text-gray-500">từ</span>
              </div>
              <div className="font-mono text-[11px] text-gray-500">
                AI: {aiGen} · Dict: {dictMatched}
              </div>
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: "Dữ liệu Log",
      key: "actions",
      align: "center",
      width: 110,
      render: (_, record, index) => {
        const hasData = Boolean(record.raw_input_path || record.output_path || record.payload)
        if (!hasData) return <Text type="secondary">—</Text>

        return (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(record, index)}
            className="px-2 text-xs"
          >
            Chi tiết
          </Button>
        )
      },
    },
    {
      title: "Thiết bị",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 130,
      align: "center",
      render: (ua: string | null | undefined, record) => (
        <DeviceBadge userAgent={ua} ipAddress={record.ip_address} />
      ),
    },
    {
      title: "Thời gian xử lý",
      dataIndex: "duration_ms",
      key: "duration_ms",
      width: 110,
      align: "center",
      render: (ms?: number | null) => {
        if (!ms) return <Text type="secondary">—</Text>
        const formatted = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
        return <span className="font-mono text-gray-700 text-xs">{formatted}</span>
      },
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 135,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<AddVocabularyLogItem>
        bordered
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

export default AddVocabularyTable
